import { Autocomplete, FormControl, FormLabel, TextField } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect } from 'react';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

const defaultTitle = 'Region';

export const RestoreRegionSelect = ({
  restoreAccountId,
  regionName,
  onChange,
  cloudProvider,
  isRequired = false,
}: {
  restoreAccountId?: string;
  regionName?: string;
  onChange: (regionName?: string) => void;
  cloudProvider: Provider;
  isRequired?: boolean;
}) => {
  const dal = useDAL();
  const title = `${defaultTitle}${isRequired ? '' : ' (optional)'}`;

  if (!restoreAccountId) {
    return <EmptySelect title={title} />;
  }

  return (
    <InnerRegionSelect
      accountId={restoreAccountId}
      regionName={regionName}
      onChange={onChange}
      useRegionsCallback={
        cloudProvider === Provider.Aws || cloudProvider === Provider.Gcp
          ? () => dal.restore.regions.useList(restoreAccountId)
          : cloudProvider === Provider.Azure
            ? () => dal.restore.azureRestoreConfig.useListRegions()
            : () => {
                throw new Error('Unsupported cloud provider');
              }
      }
      title={title}
    />
  );
};

const InnerRegionSelect = ({
  useRegionsCallback,
  regionName,
  onChange,
  title,
}: {
  accountId: string;
  regionName?: string;
  onChange: (regionName?: string) => void;
  useRegionsCallback:
    | (() => ReturnType<
        ReturnType<typeof useDAL>['restore']['regions']['useList']
      >)
    | (() => ReturnType<
        ReturnType<
          typeof useDAL
        >['restore']['azureRestoreConfig']['useListRegions']
      >);
  title: string;
}) => {
  const { data: body, isLoading } = useRegionsCallback();

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (regionName && !body?.regions.includes(regionName)) {
      onChange(undefined);
    }
  }, [body?.regions, isLoading, onChange, regionName]);

  if (!body?.regions.length) {
    return <EmptySelect title={title} />;
  }

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>{title}</FormLabel>
      <Autocomplete
        clearOnBlur
        size='small'
        value={regionName || null}
        onChange={(event, newValue) => onChange(newValue || '')}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder='Select'
              label=''
              slotProps={{ inputLabel: { shrink: true } }}
            />
          );
        }}
        options={body.regions}
      />
    </FormControl>
  );
};
